// Chat list (Nachrichten): the row model of the mobile Pro chat list
// (Kjuup-Mobile-App src/components/home/v2/messages: chatRowModel.js,
// pro/ChatRowPro.js, pro/proChatLive.js, ui/pro/entityTone.js), as pure helpers.
import moment from "moment";
import { isDirectMessageChat, isSelfChatGroup } from "../../utils/conversationUtils";
import { pickOversightCompany } from "../../utils/roomOversight";
import { isTeamsChat, isTeamsOneOnOne } from "../../utils/teams/teamsChats";

const sameId = (a, b) => a != null && b != null && String(a) === String(b);

/**
 * Row kind: "self" (My Space), "direct" (a person), "room" (a room's own
 * chat, the defaultChatGroup) or "group" (a group inside a room).
 */
export function chatRowKind(item) {
  if (!item) return "direct";
  // A Microsoft Teams mirror is a group on the server (isOneToOne false, no room): a Teams 1:1 is
  // shown like a direct chat, a Teams group chat like a group. Never My Space.
  if (isTeamsChat(item)) return isTeamsOneOnOne(item) ? "direct" : "group";
  if (isSelfChatGroup(item)) return "self";
  if (isDirectMessageChat(item)) return "direct";
  return item.defaultChatGroup ? "room" : "group";
}

/** A Kjuup Pilot chat: the Pilot chat was removed, a payload row that still marks one is left out. */
export function isPilotChat(item) {
  if (!item) return false;
  const g = item.group || {};
  const name = String(g.name || g.groupName || "").trim();
  return !!(item.isPilot || g.isPilot || name === "Kjuup Pilot");
}

/** Chat row time: today HH:mm, otherwise the short date (DD.MM.YY / MM/DD/YY). */
export function formatChatListTime(value, shortDateFormat) {
  if (!value) return "";
  const m = moment(value);
  if (!m.isValid()) return "";
  if (m.isSame(moment(), "day")) return m.format("HH:mm");
  return m.format(shortDateFormat || "DD.MM.YY");
}

/**
 * Mail row time: today HH:mm, yesterday, this year day + short month, older
 * with the year. Month names come from Intl, so moment's global locale stays untouched.
 */
export function formatMailListTime(value, { yesterday = "Yesterday", locale = "en-GB" } = {}) {
  if (!value) return "";
  const m = moment(value);
  if (!m.isValid()) return "";
  const now = moment();
  if (m.isSame(now, "day")) return m.format("HH:mm");
  if (m.isSame(now.clone().subtract(1, "day"), "day")) {
    const label = String(yesterday || "");
    return label.charAt(0).toUpperCase() + label.slice(1);
  }
  const options = m.isSame(now, "year")
    ? { day: "2-digit", month: "short" }
    : { day: "2-digit", month: "short", year: "numeric" };
  try {
    return new Intl.DateTimeFormat(locale, options).format(m.toDate());
  } catch (e) {
    return m.format(m.isSame(now, "year") ? "DD MMM" : "DD MMM YYYY");
  }
}

/** First name of a person ("Dr. Lisa Schneider" -> "Lisa"): the first word that is no title. */
export function firstNameOf(full) {
  const words = String(full || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  return words.find((w) => !/\.$/.test(w)) || words[0] || "";
}

/** Two-letter initials. */
export function initialsOf(name) {
  return String(name || "")
    .trim()
    .split(/\s+/)
    .map((w) => w.replace(/^[^0-9A-Za-zÀ-ÖØ-öø-ÿ]+/, ""))
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");
}

// The person avatars' pastel backgrounds (mobile ProKit PASTELS), with dark initials.
const PASTELS = ["#FFE2CC", "#DCEBFF", "#FFE7D6", "#E8E1FF", "#DDF3E8", "#FCE1EC", "#E1F2F7"];
export const AVATAR_INK = "#17222E";

export function pastelFor(key) {
  let h = 0;
  const s = String(key || "");
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return PASTELS[h % PASTELS.length];
}

/** The colour of a room / group row: a room chat uses the room's colour, a group its own first. */
export function chatColorOf(item, { room } = {}) {
  if (!item) return null;
  const group = item.group && typeof item.group === "object" ? item.group.themeColor : null;
  const building = item.buildingThemeColor;
  return (room ? building || group : group || building) || null;
}

function parseHex(value) {
  const raw = String(value || "").trim().replace(/^#/, "");
  const hex = raw.length === 3 ? raw.split("").map((c) => c + c).join("") : raw;
  if (!/^[0-9a-f]{6}$/i.test(hex)) return null;
  return [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16));
}

const toHex = (rgb) => `#${rgb.map((v) => Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, "0")).join("")}`;
const mix = (a, b, t) => a.map((v, i) => v + (b[i] - v) * t);

function luminance(rgb) {
  const [r, g, b] = rgb.map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function contrast(a, b) {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

const toneCache = new Map();

/**
 * A room / group tile in its own colour (mobile entityTone): a light tint of the
 * colour on white, the initials / glyph in the colour darkened until it reads
 * (4.5:1). null when the value is no hex colour.
 */
export function toneOf(value) {
  const key = String(value || "");
  if (toneCache.has(key)) return toneCache.get(key);
  const rgb = parseHex(value);
  let tone = null;
  if (rgb) {
    const white = [255, 255, 255];
    const black = [0, 0, 0];
    const bg = mix(white, rgb, 0.14);
    let fg = rgb;
    for (let i = 0; i < 20 && contrast(fg, bg) < 4.5; i++) fg = mix(fg, black, 0.08);
    tone = { bg: toHex(bg), fg: toHex(fg) };
  }
  toneCache.set(key, tone);
  return tone;
}

/** The other person of a direct chat (group.otherUserId, group.users, then the last sender). */
export function partnerIdOf(item, meId) {
  const g = item && item.group;
  if (!g || isSelfChatGroup(item)) return null;
  if (!isDirectMessageChat(item)) return null;
  if (g.otherUserId != null && !sameId(g.otherUserId, meId)) return String(g.otherUserId);
  const users = Array.isArray(g.users) ? g.users : [];
  const other = users.find((u) => u != null && !sameId(u.id != null ? u.id : u, meId));
  if (other != null) return String(other.id != null ? other.id : other);
  if (item.senderId != null && !sameId(item.senderId, meId)) return String(item.senderId);
  return null;
}

const isMutedRow = (it) => !!(it && it.group && (it.group.isMute === true || it.group.isMute === 1 || it.group.isMute === "1"));

/**
 * A listed chat that counts as unread: unread messages, not muted (as /badge/count),
 * and not open (opening one clears it at once). `countOpened` keeps the open
 * chat counting away from the Messages page, where it is not being read.
 */
const isUnreadRow = (it, countOpened = false) =>
  !!it && !it._isEmail && (countOpened || !it.isOpened) && !isMutedRow(it) && (Number(it.unseenChatCount) || 0) > 0;

/**
 * Chats with unread messages, for the Unread chip and the sidebar's Messages
 * badge: the listed chats by their own counts, plus the /badge/count `chat`
 * entries (non-muted chats with unread messages) that are not listed yet.
 */
export function countUnreadChats(rows, badgeChats, { countOpened = false } = {}) {
  const listed = new Set();
  const unread = new Set();
  (rows || []).forEach((it) => {
    if (!it || it._isEmail || !it.group || it.group.id == null) return;
    const id = String(it.group.id);
    listed.add(id);
    if (isUnreadRow(it, countOpened)) unread.add(id);
  });
  (Array.isArray(badgeChats) ? badgeChats : []).forEach((entry) => {
    const id = entry && entry.group != null ? String(entry.group) : null;
    if (id && !listed.has(id) && (Number(entry.unreadCounts) || 0) > 0) unread.add(id);
  });
  return unread.size;
}

/**
 * Unread chats per chip, by the same rows and rule as the Unread chip, so the
 * chips add up: Direct (My Space and Teams 1:1 included), Rooms (a room's own
 * chat) and Groups (the groups inside rooms), each split Internal / External
 * for the sub-chips. Chats not listed yet have no kind and only count in Unread.
 * Teams counts the Microsoft Teams chats, which also stay in Direct / Groups.
 */
export function unreadChatsByChip(rows) {
  const scope = () => ({ all: 0, internal: 0, external: 0 });
  const out = { direct: 0, rooms: scope(), groups: scope(), teams: 0 };
  const seen = new Set();
  (rows || []).forEach((it) => {
    if (!isUnreadRow(it) || !it.group || it.group.id == null) return;
    const id = String(it.group.id);
    if (seen.has(id)) return;
    seen.add(id);
    if (isTeamsChat(it)) out.teams += 1;
    const kind = chatRowKind(it);
    if (kind === "direct" || kind === "self") {
      out.direct += 1;
      return;
    }
    const bucket = kind === "room" ? out.rooms : out.groups;
    bucket.all += 1;
    if (it.isInternal) bucket.internal += 1;
    else bucket.external += 1;
  });
  return out;
}

/**
 * The room behind a room's own chat row, in the shape of the profile's pinned
 * rooms. `full` is the room from the room lists (it knows isJoin / isDone).
 * null for any other row.
 */
export function roomOfChatRow(item, full) {
  if (!item || !item.group || isDirectMessageChat(item) || !item.defaultChatGroup) return null;
  const id = item.buildingList != null && item.buildingList !== "" ? item.buildingList : item.building;
  if (id == null || id === "") return null;
  const isTrue = (v) => v === true || v === 1 || v === "1";
  return {
    id,
    name: (full && full.name) || item.buildingName || "",
    image: (full && full.image) || item.buildingImage || null,
    themeColor: (full && full.themeColor) || item.buildingThemeColor || null,
    isInternal: isTrue(item.isInternal) || !!(full && full.isInternal),
    isJoin: full ? !!full.isJoin : item.group.isLeave !== true && item.group.isLeave !== 1,
    isDone: full ? !!full.isDone : !!item.isDone,
  };
}

export const isRoomPinned = (pins, roomId) => (Array.isArray(pins) ? pins : []).some((p) => p && sameId(p.id, roomId));

/** The id of a row / company, or null. */
export const idOf = (x) => (x && x.id != null ? x.id : null);

/** The /badge/count `chat` list (non-muted chats with unread messages). */
export const badgeChatsOf = (counts) => (counts && Array.isArray(counts.chat) ? counts.chat : null);

/**
 * Rooms > Intern with more than one Kjuup+ company: the company whose room
 * chats show (the last pick, else the app-wide company, else the first). null
 * when the filter does not apply.
 */
export function pickRoomCompany({ scope, sub, companies, picked, appCompanyId }) {
  if (scope !== "rooms" || sub !== "internal" || !Array.isArray(companies) || companies.length < 2) return null;
  return pickOversightCompany(companies, picked, appCompanyId);
}

/**
 * Rooms > Verwaltung: on while that sub-chip is picked by a viewer who manages
 * company rooms; the company shown and its key.
 */
export function resolveOversight({ activeFilter, scope, sub, unreadOnly, companies, picked, appCompanyId }) {
  const on =
    activeFilter === "groups" &&
    scope === "rooms" &&
    sub === "oversight" &&
    !unreadOnly &&
    Array.isArray(companies) &&
    companies.length > 0;
  const company = on ? pickOversightCompany(companies, picked, appCompanyId) : null;
  return { on: !!company, company, key: company ? String(company.id) : null };
}

/** Fills `{name}`-style placeholders. */
export function fillTemplate(text, vars) {
  return String(text == null ? "" : text).replace(/\{(\w+)\}/g, (m, k) => (vars && vars[k] != null ? String(vars[k]) : m));
}
